'use client';
import { useRef } from 'react';
import { motion, useInView } from 'motion/react';
import {
  Factory,
  Calculator,
  Globe,
  FileBarChart,
  Layers,
  Shield,
  Sparkles,
} from 'lucide-react';

const features = [
  {
    icon: Factory,
    title: 'OT-First Design',
    description:
      'Pre-built templates for SCADA, PLCs, DCS, HMIs, and Safety Instrumented Systems. Not an IT tool adapted for OT.',
    highlight: 'Industrial templates',
    gradient: 'from-[hsl(var(--amber))] to-[hsl(var(--amber-light))]',
  },
  {
    icon: Calculator,
    title: 'Financial Quantification',
    description:
      'Calculate actual currency values, not High/Medium/Low ratings. Know exactly what downtime costs per hour.',
    highlight: 'Real numbers',
    gradient: 'from-[hsl(var(--slate-dark))] to-[hsl(var(--slate-medium))]',
  },
  {
    icon: Globe,
    title: 'Multi-Currency Support',
    description:
      '25+ currencies with proper formatting. Select once, used everywhere—inputs, calculations, and reports.',
    highlight: '25+ currencies',
    gradient: 'from-[hsl(var(--teal))] to-[hsl(var(--teal-light))]',
  },
  {
    icon: FileBarChart,
    title: 'Board-Ready Reports',
    description:
      'Executive summaries with financial figures, not technical jargon. Investment justification packs included.',
    highlight: 'Executive ready',
    gradient: 'from-[hsl(var(--amber))] to-[hsl(var(--amber-glow))]',
  },
  {
    icon: Layers,
    title: 'Cascade Analysis',
    description:
      'Map system dependencies and see how failure cascades. Understand total exposure when critical systems fail.',
    highlight: 'Dependency mapping',
    gradient: 'from-[hsl(var(--slate-dark))] to-[hsl(var(--slate-medium))]',
  },
  {
    icon: Shield,
    title: 'Risk Tier Classification',
    description:
      'Automatic classification into Tier 1 (Critical), Tier 2 (High), and Tier 3 (Moderate) based on MTD and RTO.',
    highlight: 'Auto-classification',
    gradient: 'from-[hsl(var(--teal))] to-[hsl(var(--teal-light))]',
  },
];

function FeatureCard({
  feature,
  index,
  isInView,
}: {
  feature: (typeof features)[0];
  index: number;
  isInView: boolean;
}) {
  const Icon = feature.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="group relative"
    >
      <div className="relative h-full bg-white rounded-2xl p-6 border border-[hsl(var(--border))] transition-all duration-300 hover:shadow-xl hover:border-[hsl(var(--amber))]/30 hover:-translate-y-1">
        {/* Gradient accent on hover */}
        <div
          className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-[0.03] transition-opacity duration-300`}
        />

        {/* Icon */}
        <div
          className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-5 shadow-lg group-hover:scale-110 transition-transform duration-300`}
        >
          <Icon className="w-6 h-6 text-white" />
        </div>

        {/* Content */}
        <div className="relative">
          <h3 className="text-lg font-serif font-medium text-[hsl(var(--slate-deep))] mb-2">
            {feature.title}
          </h3>

          <p className="text-[hsl(var(--slate-medium))] text-sm leading-relaxed mb-4">
            {feature.description}
          </p>

          {/* Highlight tag */}
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[hsl(var(--muted))] text-xs font-medium text-[hsl(var(--slate-dark))]">
            <Sparkles className="w-3 h-3" />
            {feature.highlight}
          </span>
        </div>

        {/* Corner accent */}
        <div className="absolute top-0 right-0 w-16 h-16 overflow-hidden rounded-tr-2xl">
          <div
            className={`absolute top-0 right-0 w-8 h-8 bg-gradient-to-bl ${feature.gradient} opacity-10 transform translate-x-4 -translate-y-4`}
          />
        </div>
      </div>
    </motion.div>
  );
}

export default function Features() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} id="features" className="relative py-24 lg:py-32">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-mesh-hero" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[hsl(var(--slate-dark))]/5 border border-[hsl(var(--slate-dark))]/10 text-sm font-medium text-[hsl(var(--slate-dark))] mb-6">
            <Sparkles className="w-4 h-4" />
            Capabilities
          </span>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-medium text-[hsl(var(--slate-deep))] mb-6 text-balance">
            Everything You Need for{' '}
            <span className="text-gradient-amber">Comprehensive BIA</span>
          </h2>

          <p className="text-lg text-[hsl(var(--slate-medium))] leading-relaxed">
            Purpose-built for operational technology environments with the
            financial rigor your executives demand.
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <FeatureCard
              key={feature.title}
              feature={feature}
              index={index}
              isInView={isInView}
            />
          ))}
        </div>

        {/* Bottom highlight */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-16 text-center"
        >
          <div className="inline-flex items-center gap-6 px-8 py-4 rounded-2xl bg-white border border-[hsl(var(--border))] shadow-lg">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-[hsl(var(--amber))] animate-pulse" />
              <span className="text-sm font-medium text-[hsl(var(--slate-dark))]">
                Real-time calculations
              </span>
            </div>
            <div className="w-px h-6 bg-[hsl(var(--border))]" />
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-[hsl(var(--teal))]" />
              <span className="text-sm font-medium text-[hsl(var(--slate-dark))]">
                Auto-save progress
              </span>
            </div>
            <div className="w-px h-6 bg-[hsl(var(--border))]" />
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-[hsl(var(--slate-dark))]" />
              <span className="text-sm font-medium text-[hsl(var(--slate-dark))]">
                Export anywhere
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
